import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import FormField from "components/molecules/FormField/FormField";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { unitMeasurementCmOrInch } from "pages/shipper/RequestForm/components/PackingList/PackingListDefaultOptions";

interface LWHFieldsProps {
  isRequired: boolean;
  defaultMUnit?: string;
}

function LWHFields({ isRequired, defaultMUnit }: LWHFieldsProps) {
  const { getValues, control } = useFormContext();
  const [length, width, height, mUnit] = useWatch({
    name: ["length", "width", "height", "m_unit"],
    control,
  });

  // Calculate the total CBM of the item
  const totalCBM = useMemo(() => {
    const volumeInCubicMeters =
      ["length", "width", "height"]
        .map((dim) => Number(getValues(dim)) * (mUnit === "inch" ? 2.54 : 1))
        .reduce((acc, val) => acc * val, 1) / 1000000;
    return isNaN(volumeInCubicMeters) ? 0 : volumeInCubicMeters.toFixed(2);
  }, [length, width, height, mUnit]);

  const intl = useIntl();
  const mUnitDefault = getValues("m_unit") || defaultMUnit || unitMeasurementCmOrInch(intl)[1].value;

  return (
    <div className="d-flex">
      <FormField
        label={intl.formatMessage({
          id: "new-request.packing-list.length",
          defaultMessage: "Length",
        })}
        placeholder={intl.formatMessage({
          id: "new-request.packing-list.length",
          defaultMessage: "Length",
        })}
        field_id="length"
        validations={{
          validate: {
            onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
          },
          setValueAs: (v: string) => (!v ? null : v),
          shouldUnregister: true,
        }}
        mandatory={isRequired}
        containerClasses="w-90-px"
      />
      <FormField
        label={intl.formatMessage({
          id: "new-request.packing-list.width",
          defaultMessage: "Width",
        })}
        type="textinput"
        placeholder={intl.formatMessage({
          id: "new-request.packing-list.width",
          defaultMessage: "Width",
        })}
        field_id="width"
        validations={{
          validate: {
            onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
          },
          setValueAs: (v: string) => (!v ? null : v),
          shouldUnregister: true,
        }}
        mandatory={isRequired}
        containerClasses="w-90-px ml-2"
      />
      <FormField
        label={intl.formatMessage({
          id: "new-request.packing-list.height",
          defaultMessage: "Height",
        })}
        type="textinput"
        placeholder={intl.formatMessage({
          id: "new-request.packing-list.height",
          defaultMessage: "Height",
        })}
        field_id="height"
        validations={{
          validate: {
            onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
          },
          setValueAs: (v: string) => (!v ? null : v),
          shouldUnregister: true,
        }}
        mandatory={isRequired}
        containerClasses="w-90-px ml-2"
      />
      <div className="w-90-px ml-2 pt-1">
        <Dropdown
          useHookForm
          className="mt-4"
          name="m_unit"
          options={unitMeasurementCmOrInch(intl)}
          defaultValue={mUnitDefault}
        />
      </div>
      <div className="w-90-px ml-2">
        <p className="font-b3 text-secondary">
          <FormattedMessage id="new-request.quote-confirmation.packing-list.CBM" defaultMessage="Total CBM" />
        </p>
        <p className="font-b4 mt-3">{totalCBM} m³</p>
      </div>
    </div>
  );
}

export default LWHFields;
